/**
 * MapWorkspace — the top-level map container.
 *
 * Composes MapCanvas with toolbar, search, layer panel, mode control,
 * legend, and status indicators. This is the single entry point that
 * pages use to get a full map experience.
 */

import React, { useState } from 'react';
import MapCanvas from './MapCanvas';
import MapToolbar from './MapToolbar';
import MapSearch from './MapSearch';
import MapLayerPanel from './MapLayerPanel';
import MapModeControl from './MapModeControl';
import MapStyleControl from './MapStyleControl';
import MapLegend from './MapLegend';
import MapStatus from './MapStatus';
import type {
  BasemapStyle,
  GeoCoord,
  LayerVisibility,
  MapField,
  MapTree,
  MapFarmer,
  MapCohort,
  MapWarehouse,
  MapViewport,
  MapMode,
} from '../types/mapTypes';
import type { BoundaryLevel } from '../layers/adminBoundaryLayer';
import type { Map } from 'mapbox-gl';

interface MapWorkspaceProps {
  // Viewport
  viewport: MapViewport;
  navigateRequested: boolean;
  onViewportChange: (viewport: MapViewport) => void;

  // Data
  fields: MapField[];
  trees: MapTree[];
  farmers: MapFarmer[];
  cohorts: MapCohort[];
  warehouses: MapWarehouse[];

  // Visibility
  visibility: LayerVisibility;
  onToggleLayer: (layer: keyof LayerVisibility) => void;

  // Stats
  stats: {
    fieldCount: number;
    treeCount: number;
    farmerCount: number;
    cohortCount: number;
    warehouseCount: number;
  };

  // Selection
  selectedFieldId?: number | null;
  selectedTreeId?: number | null;
  onFieldClick?: (field: MapField) => void;
  onTreeClick?: (tree: MapTree) => void;
  onFarmerClick?: (farmer: MapFarmer) => void;
  onCohortClick?: (cohort: MapCohort) => void;
  onWarehouseClick?: (warehouse: MapWarehouse) => void;
  onClusterClick?: (clusterId: number) => void;

  // Search
  onSearchSelect?: (result: any) => void;

  // Controls
  loading?: boolean;
  error?: string | null;

  // Map mode
  initialMode?: MapMode;

  // Admin boundaries
  adminBoundaryLevel?: BoundaryLevel;
  onAdminBoundaryChange?: (level: BoundaryLevel) => void;

  // Drawing
  drawingMode?: boolean;
  draftBoundary?: GeoCoord[];
  onDraftBoundaryChange?: (boundary: GeoCoord[]) => void;

  // Drawing controls
  drawingTools?: React.ReactNode;

  // Selection drawer
  selectionDrawer?: React.ReactNode;

  onMapReady?: (map: Map) => void;
}

export default function MapWorkspace({
  viewport,
  navigateRequested,
  onViewportChange,
  fields,
  trees,
  farmers,
  cohorts,
  warehouses,
  visibility,
  onToggleLayer,
  stats,
  selectedFieldId,
  selectedTreeId,
  onFieldClick,
  onTreeClick,
  onFarmerClick,
  onCohortClick,
  onWarehouseClick,
  onClusterClick,
  onSearchSelect,
  loading,
  error,
  initialMode = 'operate',
  adminBoundaryLevel = 'none',
  onAdminBoundaryChange,
  drawingMode,
  draftBoundary,
  onDraftBoundaryChange,
  drawingTools,
  selectionDrawer,
  onMapReady,
}: MapWorkspaceProps) {
  const [mode, setMode] = useState<MapMode>(initialMode);
  const [basemap, setBasemap] = useState<BasemapStyle>('terrain');
  const [showLayers, setShowLayers] = useState(false);
  const [showLegend, setShowLegend] = useState(true);

  const handleBasemapChange = (style: BasemapStyle) => {
    setBasemap(style);
  };

  const handleModeChange = (newMode: MapMode) => {
    setMode(newMode);
  };

  return (
    <div className="relative w-full h-full bg-slate-100">
      {/* The map canvas */}
      <MapCanvas
        viewport={viewport}
        navigateRequested={navigateRequested}
        basemap={basemap}
        adminBoundaryLevel={adminBoundaryLevel}
        fields={fields}
        trees={trees}
        farmers={farmers}
        cohorts={cohorts}
        warehouses={warehouses}
        visibility={visibility}
        selectedFieldId={selectedFieldId}
        selectedTreeId={selectedTreeId}
        onViewportChange={onViewportChange}
        onFieldClick={onFieldClick}
        onTreeClick={onTreeClick}
        onClusterClick={onClusterClick}
        onFarmerClick={onFarmerClick}
        onCohortClick={onCohortClick}
        onWarehouseClick={onWarehouseClick}
        onMapReady={onMapReady}
        mode={mode}
        drawingMode={drawingMode}
        draftBoundary={draftBoundary}
        onDraftBoundaryChange={onDraftBoundaryChange}
        className="absolute inset-0"
      />

      {/* Top: Search bar */}
      <div className="absolute top-4 left-4 right-4 md:right-auto md:w-96 z-10">
        <MapSearch onSelect={onSearchSelect} proximity={viewport.center} />
      </div>

      {/* Top-right: Mode control */}
      <div className="absolute top-4 right-4 z-10">
        <MapModeControl mode={mode} onChange={handleModeChange} />
      </div>

      {/* Right: Toolbar */}
      <div className="absolute top-20 right-4 z-10 flex flex-col gap-2">
        <MapToolbar
          basemap={basemap}
          onBasemapChange={handleBasemapChange}
          onToggleLayers={() => setShowLayers(!showLayers)}
          showLayersPanel={showLayers}
          onToggleLegend={() => setShowLegend(!showLegend)}
          onFitBounds={() => {
            // Emit event for parent to handle
            const allCoords = [
              ...fields.flatMap((f) => f.boundary),
              ...trees.map((t) => t.position),
              ...farmers.flatMap((f) => f.boundary),
              ...cohorts.flatMap((c) => c.boundary),
              ...warehouses.map((w) => w.position),
            ];
            if (allCoords.length > 0) {
              // Parent should handle fitBounds
              (window as any).__mapFitBounds?.(allCoords);
            }
          }}
        />
      </div>

      {/* Right: Layer panel (collapsible) */}
      {showLayers && (
        <div className="absolute top-32 right-4 z-10">
          <MapLayerPanel
            visibility={visibility}
            onToggle={onToggleLayer}
            stats={stats}
            adminBoundaryLevel={adminBoundaryLevel}
            onAdminBoundaryChange={onAdminBoundaryChange}
            onClose={() => setShowLayers(false)}
          />
        </div>
      )}

      {/* Bottom-left: Legend */}
      {showLegend && !drawingMode && (
        <div className="absolute bottom-12 left-4 z-10">
          <MapLegend visibility={visibility} />
        </div>
      )}

      {/* Bottom-left: Status */}
      <div className="absolute bottom-4 left-4 z-10">
        <MapStatus loading={loading} error={error} />
      </div>

      {/* Drawing tools overlay */}
      {drawingMode && drawingTools && (
        <div className="absolute top-20 left-4 z-10 max-w-[calc(100%-2rem)]">
          {drawingTools}
        </div>
      )}

      {/* Selection drawer */}
      {selectionDrawer && (
        <div className="absolute top-4 bottom-4 right-4 w-96 z-20">
          {selectionDrawer}
        </div>
      )}
    </div>
  );
}
